/*
 * 数式の美術館 — 点群バックドロップ
 * トップ以外の主要ページの背景に、淡い点群を全画面に漂わせる。
 *
 * 動きは流れ関数(ストリーム関数)による2次元の非圧縮流:
 *   ψ(x, y, t) = Σ Aᵢ sin(aᵢx + bᵢy + ωᵢt + φᵢ)
 *   (u, v) = (∂ψ/∂y, −∂ψ/∂x)
 * div(u,v) = 0 の理想流体で、点が渦を描きながらゆっくり流れる。
 * reduced-motion 時は静止1フレームのみ描画。
 */
(function () {
  var canvas = document.querySelector('.math-backdrop');
  if (!canvas) return;

  var ctx = canvas.getContext('2d');
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var dpr = Math.min(window.devicePixelRatio || 1, 2);
  var w, h, pts, scale;

  var style = getComputedStyle(document.body);
  var ink = (style.getPropertyValue('--color-foreground') || '17,17,17').trim();

  // 流れ関数の項: 振幅 A / 波数 (a, b) / 角速度 ω / 位相 φ
  var TERMS = [
    { A: 1.0, a: 1.3, b: 0.9, o: 0.10, p: 0.0 },
    { A: 0.8, a: -0.7, b: 1.6, o: -0.07, p: 2.1 },
    { A: 0.6, a: 2.1, b: -1.2, o: 0.13, p: 4.2 }
  ];

  // カーソル位置のガウス渦: ψ += Γ·exp(−r²/2σ²)。流れがマウスに応答する
  var mouseX = -1e9, mouseY = -1e9;
  window.addEventListener('pointermove', function (e) {
    mouseX = e.clientX * dpr;
    mouseY = e.clientY * dpr;
  });
  window.addEventListener('pointerleave', function () {
    mouseX = -1e9; mouseY = -1e9;
  });
  // タッチは指を離したら渦を消す(マウスと違い「位置」が残らないように)
  function clearTouch(e) {
    if (e.pointerType !== 'mouse') { mouseX = -1e9; mouseY = -1e9; }
  }
  window.addEventListener('pointerup', clearTouch);
  window.addEventListener('pointercancel', clearTouch);

  // タップで減衰する渦を生む(モバイルの主要インタラクション)
  var taps = [];
  window.addEventListener('pointerdown', function (e) {
    taps.push({ x: e.clientX * dpr, y: e.clientY * dpr, t0: performance.now() / 1000, sign: taps.length % 2 ? -1 : 1 });
    if (taps.length > 5) taps.shift();
  });

  // スクロール速度を流れに加える(画面を動かす=流体をかき混ぜる)
  var scrollVel = 0, lastScrollY = window.scrollY;
  window.addEventListener('scroll', function () {
    var y = window.scrollY;
    scrollVel += (y - lastScrollY) * 0.06;
    lastScrollY = y;
  }, { passive: true });

  // 速度場 (u, v) = (∂ψ/∂y, −∂ψ/∂x)。座標は scale で無次元化する
  function velocity(x, y, t) {
    var u = 0, v = 0;
    for (var i = 0; i < TERMS.length; i++) {
      var m = TERMS[i];
      var c = m.A * Math.cos(m.a * x / scale + m.b * y / scale + m.o * t + m.p);
      u += m.b * c;
      v -= m.a * c;
    }
    // ガウス渦(カーソル): u = −Γ·dy/σ²·e^(−r²/2σ²), v = Γ·dx/σ²·e^(−r²/2σ²)
    var sigma = 140 * dpr;
    var dx = x - mouseX, dy = y - mouseY;
    var r2 = dx * dx + dy * dy;
    if (r2 < sigma * sigma * 16) {
      var s = (3.0 * sigma) * Math.exp(-r2 / (2 * sigma * sigma)) / (sigma * sigma);
      u += -dy * s;
      v += dx * s;
    }
    // タップ渦: 強さが e^(−Δt/τ) で減衰(τ=1.2s、交互に逆回転)
    for (var k = 0; k < taps.length; k++) {
      var tp = taps[k];
      var age = t - tp.t0;
      if (age < 0 || age > 4) continue;
      var dxt = x - tp.x, dyt = y - tp.y;
      var rt2 = dxt * dxt + dyt * dyt;
      if (rt2 < sigma * sigma * 16) {
        var st = tp.sign * (5.0 * sigma) * Math.exp(-age / 1.2) * Math.exp(-rt2 / (2 * sigma * sigma)) / (sigma * sigma);
        u += -dyt * st;
        v += dxt * st;
      }
    }
    // スクロールの一様流(すぐ減衰)
    v -= scrollVel;
    return [u, v];
  }

  function init() {
    w = canvas.width = window.innerWidth * dpr;
    h = canvas.height = window.innerHeight * dpr;
    scale = Math.min(w, h) / 3.2; // 画面に渦が2〜3個収まる大きさ
    // 密度: 約5500px²に1点(モバイルでも軽量)
    var n = Math.round((window.innerWidth * window.innerHeight) / 5500);
    pts = [];
    for (var i = 0; i < n; i++) {
      pts.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: (0.6 + Math.random() * 1.1) * dpr
      });
    }
  }

  function draw() {
    ctx.clearRect(0, 0, w, h);
    for (var i = 0; i < pts.length; i++) {
      var p = pts[i];
      ctx.fillStyle = 'rgba(' + ink + ',' + (0.12 + (p.r / dpr - 0.6) * 0.08) + ')';
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  var SPEED = 0.3; // 移流速度(px/frame 相当)

  function tick(now) {
    var t = now / 1000;
    scrollVel *= 0.92; // スクロールの勢いは滑らかに減衰
    for (var i = 0; i < pts.length; i++) {
      var p = pts[i];
      var uv = velocity(p.x, p.y, t);
      p.x += uv[0] * SPEED * dpr;
      p.y += uv[1] * SPEED * dpr;
      // 画面外へ出たら反対側から(トーラス境界)
      if (p.x < -p.r) p.x += w + p.r * 2;
      if (p.x > w + p.r) p.x -= w + p.r * 2;
      if (p.y < -p.r) p.y += h + p.r * 2;
      if (p.y > h + p.r) p.y -= h + p.r * 2;
    }
    draw();
    rafId = null;
    if (visible) rafId = requestAnimationFrame(tick);
  }

  window.addEventListener('resize', function () {
    init();
    if (reduced) draw();
  });

  init();
  draw();
  if (reduced) return;

  // タブ非表示・画面外では止める
  var visible = true;
  var rafId = requestAnimationFrame(tick);
  document.addEventListener('visibilitychange', function () {
    visible = !document.hidden;
    if (visible && rafId == null) rafId = requestAnimationFrame(tick);
  });
})();
